import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';

const ContactPage = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    type: 'product',
    message: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  
  useEffect(() => {
    // Enable fade-in animations
    const fadeElems = document.querySelectorAll('.fade-in');
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    
    fadeElems.forEach(elem => observer.observe(elem));
    
    return () => {
      fadeElems.forEach(elem => observer.unobserve(elem));
    };
  }, []);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };
  
  const validateForm = () => {
    let errors = {};
    let isValid = true;
    
    if (!formData.company.trim()) {
      errors.company = language === 'fr' ? 'Le nom de l\'entreprise est requis' : 
                       language === 'ar' ? 'اسم الشركة مطلوب' : 
                       'Company name is required';
      isValid = false;
    }
    
    if (!formData.name.trim()) {
      errors.name = language === 'fr' ? 'Votre nom est requis' : 
                    language === 'ar' ? 'الاسم مطلوب' : 
                    'Your name is required';
      isValid = false;
    }
    
    if (!formData.email.trim()) {
      errors.email = language === 'fr' ? 'L\'email est requis' : 
                     language === 'ar' ? 'البريد الإلكتروني مطلوب' : 
                     'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = language === 'fr' ? 'Email invalide' : 
                     language === 'ar' ? 'البريد الإلكتروني غير صالح' : 
                     'Invalid email format';
      isValid = false;
    }
    
    if (!formData.phone.trim()) {
      errors.phone = language === 'fr' ? 'Le numéro de téléphone est requis' : 
                     language === 'ar' ? 'رقم الهاتف مطلوب' : 
                     'Phone number is required';
      isValid = false;
    }
    
    if (!formData.message.trim()) {
      errors.message = language === 'fr' ? 'Le message est requis' : 
                       language === 'ar' ? 'الرسالة مطلوبة' : 
                       'Message is required';
      isValid = false;
    }
    
    setFormErrors(errors);
    return isValid;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // In a real application, you would send this data to your backend
      console.log('Form data submitted:', formData);
      
      // Show success message
      setFormSubmitted(true);
      
      // Reset form
      setFormData({
        company: '',
        name: '',
        email: '',
        phone: '',
        address: '',
        type: 'product',
        message: ''
      });
    }
  };

  return (
    <div>
      {/* Page Header */}
      <section className="section" style={{
        backgroundColor: '#1a1a1a',
        color: 'white',
        paddingTop: '10rem',
        paddingBottom: '6rem',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div className="blob" style={{ top: '-20%', right: '-10%', opacity: 0.2 }}></div>
        <div className="blob" style={{ bottom: '-30%', left: '-15%', opacity: 0.2 }}></div>
        <div className="bg-pattern"></div>
        
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <h1 className="hero-title fade-in" style={{ 
            fontSize: '3.5rem',
            marginBottom: '1rem',
            background: 'linear-gradient(90deg, #ffffff, #FF9A40)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textFillColor: 'transparent'
          }}>
            {t('contact.title')}
          </h1>
          <p className="fade-in" style={{ 
            fontSize: '1.25rem',
            color: 'rgba(255, 255, 255, 0.9)',
            maxWidth: '800px'
          }}>
            {language === 'fr' 
              ? 'Contactez-nous pour en savoir plus sur nos produits et services ou pour demander un devis personnalisé.'
              : language === 'ar' 
              ? 'اتصل بنا لمعرفة المزيد عن منتجاتنا وخدماتنا أو لطلب عرض أسعار مخصص.'
              : 'Contact us to learn more about our products and services or to request a custom quote.'}
          </p>
        </div>
      </section>

      {/* Contact Form and Information */}
      <section className="section bg-white" style={{ position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute',
          top: '15%',
          left: '-5%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255, 107, 0, 0.03) 0%, rgba(255, 107, 0, 0) 70%)',
          zIndex: 1
        }}></div>
        
        <div style={{
          position: 'absolute',
          bottom: '15%',
          right: '-5%',
          width: '350px',
          height: '350px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255, 107, 0, 0.03) 0%, rgba(255, 107, 0, 0) 70%)',
          zIndex: 1
        }}></div>
        
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <span className="fade-in" style={{
              display: 'inline-block',
              padding: '0.5rem 1.5rem',
              backgroundColor: 'rgba(255, 107, 0, 0.1)',
              color: 'var(--assco-orange)',
              fontWeight: '600',
              fontSize: '0.875rem',
              borderRadius: 'var(--radius-full)',
              marginBottom: '1rem'
            }}>
              {language === 'fr' ? 'CONTACTEZ-NOUS' : 
               language === 'ar' ? 'اتصل بنا' : 
               'GET IN TOUCH'}
            </span>
            <h2 className="fade-in" style={{
              marginBottom: '1.5rem',
              fontSize: '2.25rem',
              fontWeight: '700'
            }}>
              {language === 'fr' ? 'Nous sommes là pour vous aider' : 
               language === 'ar' ? 'نحن هنا لمساعدتك' : 
               'We\'re here to help you'}
            </h2>
            <p className="subtitle fade-in" style={{ 
              maxWidth: '700px', 
              margin: '0 auto',
              fontSize: '1.125rem',
              color: 'var(--assco-gray-600)',
              lineHeight: '1.7'
            }}>
              {language === 'fr' 
                ? 'Avez-vous des questions sur nos produits ou services? Remplissez le formulaire ci-dessous et notre équipe vous répondra rapidement.'
                : language === 'ar' 
                ? 'هل لديك أسئلة حول منتجاتنا أو خدماتنا؟ املأ النموذج أدناه وسيرد فريقنا عليك في أقرب وقت ممكن.'
                : 'Do you have questions about our products or services? Fill out the form below and our team will respond to you promptly.'}
            </p>
          </div>
          
          <div className="grid" style={{ 
            gridTemplateColumns: 'repeat(12, 1fr)',
            gap: '3rem' 
          }}>
            {/* Contact Form */}
            <div className="col-span-7 md-col-span-12 fade-in">
              <div className="card" style={{ 
                padding: '2.5rem',
                boxShadow: '0 20px 30px rgba(0, 0, 0, 0.08)',
                borderRadius: 'var(--radius-xl)',
                border: '1px solid var(--assco-gray-100)'
              }}>
                <h2 style={{ marginBottom: '2rem' }}>
                  {language === 'fr' ? 'Envoyez-nous un message' : 
                   language === 'ar' ? 'أرسل لنا رسالة' : 
                   'Send us a message'}
                </h2>
                
                {formSubmitted ? (
                  <div style={{
                    padding: '2rem',
                    backgroundColor: 'rgba(0, 200, 83, 0.1)',
                    borderRadius: 'var(--radius-md)',
                    textAlign: 'center'
                  }}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-16 w-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    
                    <h3 style={{ 
                      fontSize: '1.5rem', 
                      marginTop: '1rem',
                      marginBottom: '0.5rem',
                      color: 'var(--assco-gray-900)'
                    }}>
                      {language === 'fr' ? 'Message envoyé avec succès!' : 
                       language === 'ar' ? 'تم إرسال الرسالة بنجاح!' : 
                       'Message sent successfully!'}
                    </h3>
                    
                    <p style={{ marginBottom: '1.5rem' }}>
                      {language === 'fr' 
                        ? 'Merci de nous avoir contactés. Notre équipe vous répondra dans les plus brefs délais.' 
                        : language === 'ar' 
                        ? 'شكرًا للتواصل معنا. سيرد عليك فريقنا في أقرب وقت ممكن.' 
                        : 'Thank you for contacting us. Our team will get back to you as soon as possible.'}
                    </p>
                    
                    <button 
                      onClick={() => setFormSubmitted(false)} 
                      style={{
                        padding: '0.5rem 1.5rem',
                        backgroundColor: 'var(--assco-orange)',
                        color: 'white',
                        borderRadius: 'var(--radius-md)',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      {language === 'fr' ? 'Envoyer un autre message' : 
                       language === 'ar' ? 'إرسال رسالة أخرى' : 
                       'Send another message'}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="grid" style={{ 
                      gridTemplateColumns: '1fr 1fr',
                      gap: '1.5rem',
                      marginBottom: '1.5rem'
                    }}>
                      <div style={{ gridColumn: language === 'ar' ? '2 / 3' : '1 / 2' }}>
                        <label className="form-label">
                          {language === 'fr' ? 'Nom de l\'entreprise' : 
                           language === 'ar' ? 'اسم الشركة' : 
                           'Company Name'}*
                        </label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          className={`form-input ${formErrors.company ? 'form-input-error' : ''}`}
                          style={{
                            width: '100%',
                            padding: '0.9rem 1.2rem',
                            border: formErrors.company ? '2px solid #e53e3e' : '1px solid var(--assco-gray-300)',
                            borderRadius: 'var(--radius-lg)',
                            fontSize: '1rem',
                            transition: 'all 0.3s ease'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = 'var(--assco-orange)';
                            e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 0, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = formErrors.company ? '#e53e3e' : 'var(--assco-gray-300)';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                        {formErrors.company && (
                          <p style={{ color: '#e53e3e', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                            {formErrors.company}
                          </p>
                        )}
                      </div>
                      
                      <div style={{ gridColumn: language === 'ar' ? '1 / 2' : '2 / 3' }}>
                        <label className="form-label">
                          {language === 'fr' ? 'Votre nom' : 
                           language === 'ar' ? 'اسمك' : 
                           'Your Name'}*
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className={`form-input ${formErrors.name ? 'form-input-error' : ''}`}
                          style={{
                            width: '100%',
                            padding: '0.9rem 1.2rem',
                            border: formErrors.name ? '2px solid #e53e3e' : '1px solid var(--assco-gray-300)',
                            borderRadius: 'var(--radius-lg)',
                            fontSize: '1rem',
                            transition: 'all 0.3s ease'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = 'var(--assco-orange)';
                            e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 0, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = formErrors.name ? '#e53e3e' : 'var(--assco-gray-300)';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                        {formErrors.name && (
                          <p style={{ color: '#e53e3e', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                            {formErrors.name}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid" style={{ 
                      gridTemplateColumns: '1fr 1fr',
                      gap: '1.5rem',
                      marginBottom: '1.5rem'
                    }}>
                      <div style={{ gridColumn: language === 'ar' ? '2 / 3' : '1 / 2' }}>
                        <label className="form-label">
                          {language === 'fr' ? 'Email' : 
                           language === 'ar' ? 'البريد الإلكتروني' : 
                           'Email'}*
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={`form-input ${formErrors.email ? 'form-input-error' : ''}`}
                          style={{
                            width: '100%',
                            padding: '0.9rem 1.2rem',
                            border: formErrors.email ? '2px solid #e53e3e' : '1px solid var(--assco-gray-300)',
                            borderRadius: 'var(--radius-lg)',
                            fontSize: '1rem',
                            transition: 'all 0.3s ease'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = 'var(--assco-orange)';
                            e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 0, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = formErrors.email ? '#e53e3e' : 'var(--assco-gray-300)';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                        {formErrors.email && (
                          <p style={{ color: '#e53e3e', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                            {formErrors.email}
                          </p>
                        )}
                      </div>
                      
                      <div style={{ gridColumn: language === 'ar' ? '1 / 2' : '2 / 3' }}>
                        <label className="form-label">
                          {language === 'fr' ? 'Téléphone' : 
                           language === 'ar' ? 'الهاتف' : 
                           'Phone'}*
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className={`form-input ${formErrors.phone ? 'form-input-error' : ''}`}
                          style={{
                            width: '100%',
                            padding: '0.9rem 1.2rem',
                            border: formErrors.phone ? '2px solid #e53e3e' : '1px solid var(--assco-gray-300)',
                            borderRadius: 'var(--radius-lg)',
                            fontSize: '1rem',
                            transition: 'all 0.3s ease'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = 'var(--assco-orange)';
                            e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 0, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = formErrors.phone ? '#e53e3e' : 'var(--assco-gray-300)';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                        {formErrors.phone && (
                          <p style={{ color: '#e53e3e', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                            {formErrors.phone}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: '1.5rem' }}>
                      <label className="form-label">
                        {language === 'fr' ? 'Adresse' : 
                         language === 'ar' ? 'العنوان' : 
                         'Address'}
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className="form-input"
                        style={{
                          width: '100%',
                          padding: '0.9rem 1.2rem',
                          border: '1px solid var(--assco-gray-300)',
                          borderRadius: 'var(--radius-lg)',
                          fontSize: '1rem',
                          transition: 'all 0.3s ease'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = 'var(--assco-orange)';
                          e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 0, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'var(--assco-gray-300)';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </div>
                    
                    <div style={{ marginBottom: '1.5rem' }}>
                      <label className="form-label">
                        {language === 'fr' ? 'Type de demande' : 
                         language === 'ar' ? 'نوع الطلب' : 
                         'Request Type'}
                      </label>
                      <select
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                        className="form-select"
                        style={{
                          width: '100%',
                          padding: '0.9rem 1.2rem',
                          border: '1px solid var(--assco-gray-300)',
                          borderRadius: 'var(--radius-lg)',
                          fontSize: '1rem',
                          backgroundColor: 'white',
                          appearance: 'none',
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23555' viewBox='0 0 16 16'%3E%3Cpath fill-rule='evenodd' d='M8 10.587l4.293-4.293a1 1 0 1 1 1.414 1.414l-5 5a1 1 0 0 1-1.414 0l-5-5a1 1 0 0 1 1.414-1.414L8 10.587z'/%3E%3C/svg%3E")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 1rem center',
                          paddingRight: '3rem',
                          transition: 'all 0.3s ease'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = 'var(--assco-orange)';
                          e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 0, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'var(--assco-gray-300)';
                          e.target.style.boxShadow = 'none';
                        }}
                      >
                        <option value="product">
                          {language === 'fr' ? 'Produits' : 
                           language === 'ar' ? 'المنتجات' : 
                           'Products'}
                        </option>
                        <option value="service">
                          {language === 'fr' ? 'Services' : 
                           language === 'ar' ? 'الخدمات' : 
                           'Services'}
                        </option>
                        <option value="quote">
                          {language === 'fr' ? 'Demande de devis' : 
                           language === 'ar' ? 'طلب عرض أسعار' : 
                           'Request for quote'}
                        </option>
                        <option value="support">
                          {language === 'fr' ? 'Support technique' : 
                           language === 'ar' ? 'الدعم الفني' : 
                           'Technical support'}
                        </option>
                        <option value="other">
                          {language === 'fr' ? 'Autre' : 
                           language === 'ar' ? 'أخرى' : 
                           'Other'}
                        </option>
                      </select>
                    </div>
                    
                    <div style={{ marginBottom: '2rem' }}>
                      <label className="form-label">
                        {language === 'fr' ? 'Votre message' : 
                         language === 'ar' ? 'رسالتك' : 
                         'Your Message'}*
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows="5"
                        className={`form-textarea ${formErrors.message ? 'form-textarea-error' : ''}`}
                        style={{
                          width: '100%',
                          padding: '1rem 1.2rem',
                          border: formErrors.message ? '2px solid #e53e3e' : '1px solid var(--assco-gray-300)',
                          borderRadius: 'var(--radius-lg)',
                          fontSize: '1rem',
                          resize: 'vertical',
                          transition: 'all 0.3s ease',
                          lineHeight: '1.7'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = 'var(--assco-orange)';
                          e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 0, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = formErrors.message ? '#e53e3e' : 'var(--assco-gray-300)';
                          e.target.style.boxShadow = 'none';
                        }}
                      ></textarea>
                      {formErrors.message && (
                        <p style={{ color: '#e53e3e', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                          {formErrors.message}
                        </p>
                      )}
                    </div>
                    
                    <div style={{ textAlign: language === 'ar' ? 'left' : 'right' }}>
                      <button 
                        type="submit"
                        style={{
                          padding: '1rem 2.75rem',
                          backgroundColor: 'var(--assco-orange)',
                          color: 'white',
                          borderRadius: 'var(--radius-xl)',
                          fontWeight: '600',
                          cursor: 'pointer',
                          border: 'none',
                          fontSize: '1.05rem',
                          boxShadow: '0 10px 15px -3px rgba(255, 107, 0, 0.2)',
                          transition: 'all 0.3s ease',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.transform = 'translateY(-3px)';
                          e.target.style.boxShadow = '0 15px 20px -5px rgba(255, 107, 0, 0.3)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 10px 15px -3px rgba(255, 107, 0, 0.2)';
                        }}
                      >
                        {language === 'fr' ? 'Envoyer le message' : 
                         language === 'ar' ? 'إرسال الرسالة' : 
                         'Send Message'}
                        <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '20px', height: '20px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
            
            {/* Contact Information */}
            <div className="col-span-5 md-col-span-12 fade-in" style={{ animationDelay: '0.3s' }}>
              <div style={{ height: '100%' }}>
                <div className="card" style={{ 
                  padding: '2.5rem',
                  backgroundImage: 'linear-gradient(135deg, var(--assco-gray-900) 0%, #1e2a3a 100%)',
                  color: 'white',
                  borderRadius: 'var(--radius-xl)',
                  height: '100%',
                  boxShadow: '0 20px 30px rgba(0, 0, 0, 0.15)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Decorative pattern */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '200px',
                    height: '200px',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.05' fill-rule='evenodd'/%3E%3C/svg%3E")`,
                    opacity: 0.5
                  }}></div>
                  <h2 style={{ 
                    color: 'white',
                    marginBottom: '2rem',
                    fontSize: '1.75rem',
                    position: 'relative',
                    paddingBottom: '1rem',
                    fontWeight: '600',
                    zIndex: 5
                  }}>
                    {language === 'fr' ? 'Informations de contact' : 
                     language === 'ar' ? 'معلومات الاتصال' : 
                     'Contact Information'}
                    <span style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '50px',
                      height: '3px',
                      backgroundColor: 'var(--assco-orange)',
                      borderRadius: '3px'
                    }}></span>
                  </h2>
                  
                  <div style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2.5rem',
                    position: 'relative',
                    zIndex: 5
                  }}>
                    {/* Address */}
                    <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start' }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 107, 0, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        boxShadow: '0 8px 16px rgba(255, 107, 0, 0.15)',
                        transform: 'rotate(-5deg)'
                      }}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-assco-orange" viewBox="0 0 20 20" fill="var(--assco-orange)">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <h3 style={{ 
                          color: 'white', 
                          marginBottom: '0.75rem', 
                          fontSize: '1.125rem',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <span style={{
                            width: '8px',
                            height: '8px',
                            backgroundColor: 'var(--assco-orange)',
                            borderRadius: '50%'
                          }}></span>
                          {language === 'fr' ? 'Adresse' : 
                           language === 'ar' ? 'العنوان' : 
                           'Address'}
                        </h3>
                        <p style={{ 
                          color: 'rgba(255, 255, 255, 0.9)', 
                          fontSize: '1rem',
                          lineHeight: '1.7',
                          marginLeft: '1rem',
                          borderLeft: '2px dashed rgba(255, 107, 0, 0.3)',
                          paddingLeft: '1rem'
                        }}>
                          Cité 392 Logts LSP,<br />
                          Tala Ouariane, Bejaia,<br />
                          Algérie
                        </p>
                      </div>
                    </div>
                    
                    {/* Phone */}
                    <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start' }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 107, 0, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        boxShadow: '0 8px 16px rgba(255, 107, 0, 0.15)',
                        transform: 'rotate(-5deg)'
                      }}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-assco-orange" viewBox="0 0 20 20" fill="var(--assco-orange)">
                          <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                        </svg>
                      </div>
                      <div>
                        <h3 style={{ 
                          color: 'white', 
                          marginBottom: '0.75rem', 
                          fontSize: '1.125rem',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <span style={{
                            width: '8px',
                            height: '8px',
                            backgroundColor: 'var(--assco-orange)',
                            borderRadius: '50%'
                          }}></span>
                          {language === 'fr' ? 'Téléphone' : 
                           language === 'ar' ? 'الهاتف' : 
                           'Phone'}
                        </h3>
                        <div style={{ 
                          marginLeft: '1rem',
                          borderLeft: '2px dashed rgba(255, 107, 0, 0.3)',
                          paddingLeft: '1rem'
                        }}>
                          <p style={{ 
                            color: 'rgba(255, 255, 255, 0.9)', 
                            fontSize: '0.9rem',
                            lineHeight: '1.7',
                            whiteSpace: 'nowrap'
                          }}>
                            +213 661462837 / 0658 65 83 64
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Email */}
                    <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start' }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 107, 0, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        boxShadow: '0 8px 16px rgba(255, 107, 0, 0.15)',
                        transform: 'rotate(-5deg)'
                      }}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-assco-orange" viewBox="0 0 20 20" fill="var(--assco-orange)">
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                        </svg>
                      </div>
                      <div>
                        <h3 style={{ 
                          color: 'white', 
                          marginBottom: '0.75rem', 
                          fontSize: '1.125rem',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <span style={{
                            width: '8px',
                            height: '8px',
                            backgroundColor: 'var(--assco-orange)',
                            borderRadius: '50%'
                          }}></span>
                          {language === 'fr' ? 'Email' : 
                           language === 'ar' ? 'البريد الإلكتروني' : 
                           'Email'}
                        </h3>
                        <div style={{ 
                          marginLeft: '1rem',
                          borderLeft: '2px dashed rgba(255, 107, 0, 0.3)',
                          paddingLeft: '1rem'
                        }}>
                          <a href="mailto:allaoua.felfoul@assco-dz.com" style={{ 
                            color: 'rgba(255, 255, 255, 0.9)', 
                            fontSize: '1rem',
                            lineHeight: '1.7',
                            marginBottom: '0.5rem',
                            display: 'block',
                            textDecoration: 'none',
                            transition: 'color 0.3s ease'
                          }}
                          onMouseOver={(e) => e.target.style.color = 'var(--assco-orange)'}
                          onMouseOut={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.9)'}>
                            allaoua.felfoul@assco-dz.com
                          </a>
                          <a href="mailto:felfoul.aimed.eddine@gmail.com" style={{ 
                            color: 'rgba(255, 255, 255, 0.9)', 
                            fontSize: '1rem',
                            lineHeight: '1.7',
                            display: 'block',
                            textDecoration: 'none',
                            transition: 'color 0.3s ease'
                          }}
                          onMouseOver={(e) => e.target.style.color = 'var(--assco-orange)'}
                          onMouseOut={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.9)'}>
                            felfoul.aimed.eddine@gmail.com
                          </a>
                        </div>
                      </div>
                    </div>
                    
                    {/* Working Hours */}
                    <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start' }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 107, 0, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        boxShadow: '0 8px 16px rgba(255, 107, 0, 0.15)',
                        transform: 'rotate(-5deg)'
                      }}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-assco-orange" viewBox="0 0 20 20" fill="var(--assco-orange)">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <h3 style={{ 
                          color: 'white', 
                          marginBottom: '0.75rem', 
                          fontSize: '1.125rem',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <span style={{
                            width: '8px',
                            height: '8px',
                            backgroundColor: 'var(--assco-orange)',
                            borderRadius: '50%'
                          }}></span>
                          {language === 'fr' ? 'Heures de travail' : 
                           language === 'ar' ? 'ساعات العمل' : 
                           'Working Hours'}
                        </h3>
                        <div style={{ 
                          marginLeft: '1rem',
                          borderLeft: '2px dashed rgba(255, 107, 0, 0.3)',
                          paddingLeft: '1rem'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            marginBottom: '0.75rem',
                          }}>
                            <div style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: 'rgba(255, 107, 0, 0.2)',
                              borderRadius: '4px',
                              fontSize: '0.875rem',
                              color: '#fff',
                              fontWeight: '500'
                            }}>
                              {language === 'fr' ? 'OUVERT' : language === 'ar' ? 'مفتوح' : 'OPEN'}
                            </div>
                            <p style={{ 
                              color: 'rgba(255, 255, 255, 0.9)', 
                              fontSize: '1rem',
                              lineHeight: '1.7'
                            }}>
                              {language === 'fr' ? 'Dimanche - Jeudi: 8h00 - 17h00' : 
                               language === 'ar' ? 'الأحد - الخميس: 8:00 - 17:00' : 
                               'Sunday - Thursday: 8:00 AM - 5:00 PM'}
                            </p>
                          </div>
                          
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem'
                          }}>
                            <div style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: 'rgba(220, 38, 38, 0.2)',
                              borderRadius: '4px',
                              fontSize: '0.875rem',
                              color: '#fff',
                              fontWeight: '500'
                            }}>
                              {language === 'fr' ? 'FERMÉ' : language === 'ar' ? 'مغلق' : 'CLOSED'}
                            </div>
                            <p style={{ 
                              color: 'rgba(255, 255, 255, 0.9)', 
                              fontSize: '1rem',
                              lineHeight: '1.7'
                            }}>
                              {language === 'fr' ? 'Vendredi - Samedi: Fermé' : 
                               language === 'ar' ? 'الجمعة - السبت: مغلق' : 
                               'Friday - Saturday: Closed'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section" style={{ 
          padding: 0, 
          position: 'relative',
          marginTop: '3rem'
      }}>
        <div style={{ 
          width: '100%',
          height: '500px',
          backgroundColor: 'var(--assco-gray-100)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div className="map-pattern" style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            zIndex: 1
          }}></div>
          
          {/* Map preview box */}
          <div style={{ 
            position: 'relative',
            width: '80%',
            maxWidth: '1200px',
            height: '350px',
            backgroundColor: 'white',
            borderRadius: 'var(--radius-xl)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            zIndex: 2
          }}>
            {/* In a real application, you would embed a Google Maps iframe here */}
            <div style={{ 
              width: '100%',
              height: '100%',
              background: `radial-gradient(circle, rgba(255, 255, 255, 0.8) 0%, rgba(240, 240, 240, 0.8) 100%), 
                          url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5z' fill='%23cccccc' fill-opacity='0.2' fill-rule='evenodd'/%3E%3C/svg%3E")`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: '2rem'
            }}>
              <div style={{
                width: '70px',
                height: '70px',
                borderRadius: '50%',
                backgroundColor: 'var(--assco-orange)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.5rem',
                boxShadow: '0 10px 15px rgba(255, 107, 0, 0.2)'
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '40px', height: '40px', color: 'white' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 style={{ 
                fontSize: '1.5rem', 
                marginBottom: '1rem',
                color: 'var(--assco-gray-900)'
              }}>
                {language === 'fr' ? 'Trouvez-nous sur la carte' : 
                 language === 'ar' ? 'اعثر علينا على الخريطة' : 
                 'Find us on the map'}
              </h3>
              <p style={{ 
                fontSize: '1.125rem', 
                color: 'var(--assco-gray-600)',
                maxWidth: '500px',
                marginBottom: '2rem',
                lineHeight: '1.7'
              }}>
                {language === 'fr' 
                  ? 'Notre emplacement central à Alger vous permet de nous rendre visite facilement. Nous sommes situés à proximité des principales entreprises et industries pétrochimiques.' 
                  : language === 'ar' 
                  ? 'يتيح لك موقعنا المركزي في الجزائر زيارتنا بسهولة. نحن نقع بالقرب من الشركات والصناعات البتروكيماوية الرئيسية.' 
                  : 'Our central location in Algiers allows you to visit us easily. We are located close to major petrochemical companies and industries.'}
              </p>
              <button style={{
                backgroundColor: 'var(--assco-orange)',
                color: 'white',
                padding: '0.75rem 1.5rem',
                borderRadius: 'var(--radius-md)',
                border: 'none',
                fontSize: '1rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer',
                boxShadow: '0 4px 6px rgba(255, 107, 0, 0.2)',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 12px rgba(255, 107, 0, 0.25)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 6px rgba(255, 107, 0, 0.2)';
              }}
              onClick={() => window.open('https://maps.google.com', '_blank')}
              >
                {language === 'fr' ? 'Ouvrir Google Maps' : 
                 language === 'ar' ? 'فتح خرائط Google' : 
                 'Open Google Maps'}
                <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '18px', height: '18px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Location pin markers */}
          <div style={{ 
            position: 'absolute', 
            top: '20%',
            left: '15%',
            animation: 'float 4s ease-in-out infinite',
            zIndex: 1
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '30px', height: '30px', color: 'var(--assco-orange)' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
          </div>
          <div style={{ 
            position: 'absolute', 
            bottom: '25%',
            right: '20%',
            animation: 'float 3s ease-in-out infinite 1s',
            zIndex: 1
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '24px', height: '24px', color: 'var(--assco-orange)' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
        <style jsx>{`
          @keyframes float {
            0% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-10px);
            }
            100% {
              transform: translateY(0);
            }
          }
        `}</style>
      </section>
    </div>
  );
};

export default ContactPage;
